import { Fragment, type FunctionComponent } from "react";
import {
  CheckOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  CustomerServiceOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  InfoOutlined,
  TagOutlined,
  VideoCameraOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Avatar, Badge, Button, Space } from "antd";
import dayjs from "dayjs";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { notificationsActions } from "@fitness-app/app-store";
import {
  NotificationStatus,
  NotificationType,
  type ClientNotifications,
  type OwnerNotifications,
  type TrainerNotifications,
} from "@fitness-app/data-models/entities/Notification";
import { IngredientIssueCategory } from "@fitness-app/data-models/entities/DishIssue";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { DishFeedbackName } from "~/modules/Nutrition/DishFeedback/DishFeedbackName";
import { getDishFeedbackLink } from "~/modules/Nutrition/DishFeedback/getDishFeedbackLink";
import { getIngredientIssueLink } from "~/modules/Nutrition/DishIssues/ingredientIssues";

interface OwnProps {
  notification: ClientNotifications | OwnerNotifications | TrainerNotifications;
  closeDrawer: () => void;
}

type Props = OwnProps;

const alertType = {
  [NotificationType.ClientPurchaseProduct]: "success",
  [NotificationType.ClientTerminated]: "warning",
  [NotificationType.ClientManualPaymentCreated]: "info",
  [NotificationType.ClientManualPaymentRenewal]: "info",
  [NotificationType.ClientPaymentFailed]: "error",
  [NotificationType.ProductSubscriptionEnd]: "error",
  [NotificationType.ProductAccessPeriodEnd]: "error",
  [NotificationType.ProductPaymentRequiredAction]: "warning",
  [NotificationType.ClientRenewedSubscription]: "success",
  [NotificationType.ClientSubscriptionPlanUpdated]: "success",
  [NotificationType.ClientRenewedAccessPeriod]: "success",
  [NotificationType.ClientsImportFinished]: "success",
  [NotificationType.ClientResumeSubscriptionAtPeriodEnd]: "success",
  [NotificationType.ClientCancelSubscriptionAtPeriodEnd]: "warning",
  [NotificationType.ProductAccessPeriodEndingIn14Days]: "warning",
  [NotificationType.ProductAccessPeriodEndingIn7Days]: "warning",
  [NotificationType.ProductAccessPeriodEndingIn3Days]: "warning",
  [NotificationType.ProductAccessPeriodEndingIn1Day]: "warning",
  [NotificationType.VideoBeenUploaded]: "info",
  [NotificationType.ClientSurveyAnswersUpdated]: "info",
  [NotificationType.ClientSurveyAnswered]: "info",
  [NotificationType.ClientAddedWeightMeasurement]: "info",
  [NotificationType.ClientAddedBodyMeasurements]: "info",
  [NotificationType.ClientAddedBodyPhotos]: "info",
  [NotificationType.ClientRateWorkout]: "info",
  [NotificationType.ClientRateDish]: "info",
  [NotificationType.ClientReportedIngredientIssue]: "warning",
  [NotificationType.ClientReportedDishIssue]: "warning",
  [NotificationType.ClientProgramEndingIn1Day]: "info",
  [NotificationType.ClientProgramEndingIn1Week]: "info",
  [NotificationType.ClientSentVideoRecording]: "info",
  [NotificationType.SupportRequestAssigned]: "info",
  [NotificationType.SupportCommentMention]: "info",
  [NotificationType.SupportRequestDueSoon]: "warning",
  [NotificationType.SupportRequestOverdue]: "error",
  [NotificationType.ChallengeCompletionPending]: "warning",
  [NotificationType.ChallengeParticipantPost]: "info",
  [NotificationType.ChallengeFeedComment]: "info",
} as const;

const infoStatusColorMapper = {
  error: "bg-red-600",
  warning: "bg-yellow-600",
  info: "bg-blue-500",
  success: "bg-green-500",
};

const alertIcon = {
  [NotificationType.ClientPurchaseProduct]: TagOutlined,
  [NotificationType.ClientRenewedSubscription]: TagOutlined,
  [NotificationType.ClientRenewedAccessPeriod]: TagOutlined,
  [NotificationType.ClientSubscriptionPlanUpdated]: TagOutlined,
  [NotificationType.ClientTerminated]: WarningOutlined,
  [NotificationType.ProductPaymentRequiredAction]: WarningOutlined,
  [NotificationType.ProductSubscriptionEnd]: WarningOutlined,
  [NotificationType.ProductAccessPeriodEnd]: WarningOutlined,
  [NotificationType.ClientsImportFinished]: FileSearchOutlined,
  [NotificationType.ClientResumeSubscriptionAtPeriodEnd]: TagOutlined,
  [NotificationType.ClientCancelSubscriptionAtPeriodEnd]: WarningOutlined,
  [NotificationType.ProductAccessPeriodEndingIn14Days]: TagOutlined,
  [NotificationType.ProductAccessPeriodEndingIn7Days]: TagOutlined,
  [NotificationType.ProductAccessPeriodEndingIn3Days]: TagOutlined,
  [NotificationType.ProductAccessPeriodEndingIn1Day]: TagOutlined,
  [NotificationType.VideoBeenUploaded]: VideoCameraOutlined,
  [NotificationType.ClientSurveyAnswersUpdated]: InfoOutlined,
  [NotificationType.ClientSurveyAnswered]: InfoOutlined,
  [NotificationType.ClientAddedWeightMeasurement]: InfoOutlined,
  [NotificationType.ClientAddedBodyMeasurements]: InfoOutlined,
  [NotificationType.ClientAddedBodyPhotos]: InfoOutlined,
  [NotificationType.ClientSentVideoRecording]: InfoOutlined,
  [NotificationType.ClientRateWorkout]: InfoOutlined,
  [NotificationType.ClientRateDish]: InfoOutlined,
  [NotificationType.ClientReportedIngredientIssue]: ExclamationCircleOutlined,
  [NotificationType.ClientReportedDishIssue]: ExclamationCircleOutlined,
  [NotificationType.ClientProgramEndingIn1Day]: InfoOutlined,
  [NotificationType.ClientProgramEndingIn1Week]: InfoOutlined,
  [NotificationType.ClientManualPaymentCreated]: InfoOutlined,
  [NotificationType.ClientManualPaymentRenewal]: InfoOutlined,
  [NotificationType.ClientPaymentFailed]: WarningOutlined,
  [NotificationType.SupportRequestAssigned]: CustomerServiceOutlined,
  [NotificationType.SupportCommentMention]: CustomerServiceOutlined,
  [NotificationType.SupportRequestDueSoon]: ClockCircleOutlined,
  [NotificationType.SupportRequestOverdue]: WarningOutlined,
  [NotificationType.ChallengeCompletionPending]: FileSearchOutlined,
  [NotificationType.ChallengeParticipantPost]: CommentOutlined,
  [NotificationType.ChallengeFeedComment]: CommentOutlined,
} as const;

const NotificationItem: FunctionComponent<Props> = ({ notification, closeDrawer }) => {
  const { t } = useTranslation(["dashboard", "common"]);
  const performingAction = useAppSelector((store) => store.notifications.performingAction);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const redirectToNotification = () => {
    const { data, type, status } = notification;

    if (status === NotificationStatus.UNREAD) {
      void dispatch(notificationsActions.markAsRead(notification.id));
    }

    switch (type) {
      case NotificationType.ClientRateDish:
        navigate(getDishFeedbackLink(data.feedbackId));
        break;
      case NotificationType.ClientReportedIngredientIssue:
      case NotificationType.ClientReportedDishIssue:
        navigate(getIngredientIssueLink(data.issueId));
        break;
      case NotificationType.ClientManualPaymentCreated:
        navigate(`/products/${data.productId}/transactions`);
        break;
      // A failed charge is acted on from the client's card, so land there when we
      // know who it was; the transactions list is the fallback (TRE-1840).
      case NotificationType.ClientPaymentFailed:
        navigate(
          notification.connectedClient?.email
            ? `/products/${data.productId}/clients/${notification.connectedClient.email}`
            : `/products/${data.productId}/transactions`,
        );
        break;
      case NotificationType.VideoBeenUploaded:
        navigate(`/dashboard/${data.productId}/course/lessons/${notification.data.lessonId}`);
        break;
      case NotificationType.ClientPurchaseProduct:
      case NotificationType.ClientRenewedSubscription:
      case NotificationType.ClientRenewedAccessPeriod:
      case NotificationType.ClientResumeSubscriptionAtPeriodEnd:
      case NotificationType.ClientCancelSubscriptionAtPeriodEnd:
      case NotificationType.ClientSubscriptionPlanUpdated:
      case NotificationType.ClientManualPaymentRenewal:
        navigate(
          `/products/${data.productId}/${
            notification.connectedClient?.email && "isFree" in data && !data.isFree
              ? `clients/${notification.connectedClient?.email}`
              : "clients"
          }`,
        );
        break;
      case NotificationType.ClientSurveyAnswersUpdated:
      case NotificationType.ClientSurveyAnswered:
      case NotificationType.ClientAddedWeightMeasurement:
      case NotificationType.ClientAddedBodyMeasurements:
      case NotificationType.ClientAddedBodyPhotos:
      case NotificationType.ClientRateWorkout:
      case NotificationType.ClientProgramEndingIn1Week:
      case NotificationType.ClientProgramEndingIn1Day:
        navigate(`/trainee/details/${notification.connectedClient?.traineeId}`);
        break;
      case NotificationType.ClientSentVideoRecording:
        navigate(`/trainee/details/${notification.connectedClient?.traineeId}?tab=measurements&subTab=videos`);
        break;
      case NotificationType.SupportRequestAssigned:
      case NotificationType.SupportCommentMention:
      case NotificationType.SupportRequestDueSoon:
      case NotificationType.SupportRequestOverdue:
        // ?requestId= opens the drawer for that request on the board (TRE-1549).
        navigate(`/support?requestId=${data && "requestId" in data ? data.requestId : ""}`);
        break;
      case NotificationType.ChallengeParticipantPost:
      case NotificationType.ChallengeFeedComment: {
        // Deep-link straight to the feed post/comment thread (TRE-1655), not just the challenge.
        const challengeId = data && "challengeId" in data ? data.challengeId : "";
        const postId = data && "postId" in data ? data.postId : "";
        navigate(`/challenges/${challengeId}/tablica${postId ? `?postId=${postId}` : ""}`);
        break;
      }
      case NotificationType.ChallengeCompletionPending:
        // Opens the challenge detail (the "Do akceptacji" queue lives there).
        navigate(`/challenges/${data && "challengeId" in data ? data.challengeId : ""}`);
        break;
      case NotificationType.ClientsImportFinished: {
        // TRE-2110: straight to the import summary (counters + per-row outcome).
        const productId = data && "productId" in data ? data.productId : "";
        const importId = data && "importId" in data ? data.importId : "";
        navigate(importId ? `/products/${productId}/clients/imports/${importId}` : `/products/${productId}/clients`);
        break;
      }
    }

    closeDrawer();

    return null;
  };

  const Icon = alertIcon[notification.type] ?? Fragment;

  return (
    <li
      className={cn(
        "cursor-pointer border-b px-6 py-4 hover:bg-gray-100",
        notification.status === NotificationStatus.UNREAD && "bg-gray-100/70",
      )}
      onClick={redirectToNotification}
    >
      <div className="flex space-x-3">
        <Badge dot={notification.status === NotificationStatus.UNREAD}>
          <Avatar
            className={infoStatusColorMapper[alertType[notification.type]]}
            icon={<Icon />}
            src={notification.connectedClient?.avatarUrl}
          />
        </Badge>

        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between">
            <h3
              className={cn(
                "text-sm",
                notification.type === NotificationType.ClientRateDish ||
                  notification.type === NotificationType.ClientReportedIngredientIssue ||
                  notification.type === NotificationType.ClientReportedDishIssue
                  ? "font-normal"
                  : "font-medium",
              )}
            >
              {notification.type === NotificationType.ClientReportedIngredientIssue ? (
                <Trans
                  t={t}
                  i18nKey={
                    notification.data.categories?.includes(IngredientIssueCategory.Correction)
                      ? "notificationType.client_reported_ingredient_correction"
                      : "notificationType.client_reported_ingredient_issue"
                  }
                  tOptions={{ interpolation: { escapeValue: true } }}
                  shouldUnescape
                  values={{
                    name: `${notification.connectedClient?.firstName ?? ""} ${notification.connectedClient?.lastName ?? ""}`.trim(),
                  }}
                  components={{ ingredientName: <DishFeedbackName name={notification.data.ingredientName} /> }}
                />
              ) : notification.type === NotificationType.ClientReportedDishIssue ? (
                <Trans
                  t={t}
                  i18nKey="notificationType.client_reported_dish_issue"
                  tOptions={{ interpolation: { escapeValue: true } }}
                  shouldUnescape
                  values={{
                    name: `${notification.connectedClient?.firstName ?? ""} ${notification.connectedClient?.lastName ?? ""}`.trim(),
                  }}
                  components={{ dishName: <DishFeedbackName name={notification.data.dishName} /> }}
                />
              ) : notification.type === NotificationType.ClientRateDish ? (
                <Trans
                  t={t}
                  i18nKey="notificationType.client_rate_dish"
                  tOptions={{ interpolation: { escapeValue: true } }}
                  shouldUnescape
                  values={{
                    name: `${notification.connectedClient?.firstName} ${notification.connectedClient?.lastName}`,
                    rating: notification.data.rating,
                  }}
                  components={{ dishName: <DishFeedbackName name={notification.data.dishName} /> }}
                />
              ) : (
                t(
                  `notificationType.${
                    notification.type === NotificationType.ClientPurchaseProduct && notification.data?.isFree
                      ? "client_subscribe_product"
                      : notification.type
                  }`,
                  {
                    name:
                      notification.data && "lessonName" in notification.data
                        ? notification.data.lessonName
                        : notification.data && "actorName" in notification.data
                          ? notification.data.actorName || "Ktoś z zespołu"
                          : notification.data && "authorName" in notification.data
                            ? notification.data.authorName || "Uczestniczka"
                            : `${notification.connectedClient?.firstName} ${notification.connectedClient?.lastName}`,
                    title:
                      notification.data && "requestTitle" in notification.data
                        ? notification.data.requestTitle
                        : undefined,
                    challengeName:
                      notification.data && "challengeName" in notification.data
                        ? notification.data.challengeName
                        : undefined,
                    productName:
                      notification.data && "productName" in notification.data
                        ? (notification.data.productName ?? "")
                        : "",
                    date:
                      notification.data && "periodEnd" in notification.data
                        ? dayjs.unix(notification.data.periodEnd).format("DD.MM.YYYY HH:mm")
                        : notification.data && "endDate" in notification.data
                          ? dayjs(notification.data.endDate).format("DD.MM.YYYY")
                          : undefined,
                  },
                )
              )}
            </h3>
            {notification.data && "declineReason" in notification.data && notification.data.declineReason ? (
              // The bank's reason decides what support should tell the client —
              // an exceeded BLIK limit she has to raise herself, insufficient
              // funds she only has to top up (TRE-2136).
              <p className="text-sm text-gray-600">
                {t("notificationType.payment_failed_reason", {
                  reason: notification.data.declineReason,
                  count:
                    "attemptCount" in notification.data && notification.data.attemptCount
                      ? notification.data.attemptCount
                      : 1,
                })}
              </p>
            ) : null}
            <p className="text-sm text-gray-500">
              {notification.status === NotificationStatus.UNREAD && (
                <Space>
                  <Button
                    size="small"
                    loading={performingAction?.notificationId === notification.id}
                    icon={<CheckOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      void dispatch(notificationsActions.markAsRead(notification.id));
                    }}
                  />
                </Space>
              )}
            </p>
          </div>
          {notification.type === NotificationType.ClientRateDish && notification.data.hasComment ? (
            <p className="text-sm text-gray-500">Do oceny dodano komentarz.</p>
          ) : null}
          <p className="text-sm text-gray-500">
            <Space size={4}>
              <ClockCircleOutlined /> <span>{dayjs(notification.createdAt).format("DD.MM HH:mm")}</span>
            </Space>
          </p>
        </div>
      </div>
    </li>
  );
};

export default NotificationItem;
